import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { CVHttpService } from '../../services';
import ReferenceFormDialog from '../dialogs/ReferenceFormDialog';

function ReferencesStep({ cvId }) {
    const [references, setReferences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingReference, setEditingReference] = useState(null);

    useEffect(() => {
        if (cvId) {
            loadReferences();
        }
    }, [cvId]);

    const loadReferences = async () => {
        if (!cvId) return;
        setLoading(true);
        try {
            const service = new (await import('core/services/http')).HttpService();
            const data = await service.get(`/job/cv/reference/?cv=${cvId}`, null);
            // Handle both array response and paginated response
            const referencesList = Array.isArray(data) ? data : (data?.results || []);
            setReferences(referencesList);
        } catch (err) {
            console.error('Failed to load references', err);
            setReferences([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this reference?')) return;
        try {
            await CVHttpService.deleteReference(id);
            loadReferences();
        } catch (err) {
            alert('Failed to delete reference');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Professional References
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingReference(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Reference
                </Button>
            </Stack>

            {!Array.isArray(references) || references.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                        No references added yet. Click "Add Reference" to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {Array.isArray(references) && references.map((ref) => (
                        <Paper key={ref.id} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" fontWeight={600}>
                                        {ref.name}
                                    </Typography>
                                    {ref.position && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {ref.position}
                                        </Typography>
                                    )}
                                    <Stack spacing={0.5} sx={{ mt: 2 }}>
                                        {ref.email && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <EmailIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {ref.email}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {ref.phone_number && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PhoneIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {ref.phone_number}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {ref.location && (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <LocationOnIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="text.secondary">
                                                    {ref.location}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingReference(ref);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(ref.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <ReferenceFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingReference(null);
                }}
                onSave={() => {
                    loadReferences();
                    setOpenDialog(false);
                    setEditingReference(null);
                }}
                cvId={cvId}
                reference={editingReference}
            />
        </Box>
    );
}

export default ReferencesStep;

